import { useEffect } from 'react'
import { useFormikContext } from 'formik'

import { EMPTY_LIST, NOTIFICATION_TYPE } from '../../../../constants/common'
import { FORMIK_EMPTY_VALUE } from '../../../../constants/validation'
import { isDocFlowMappingIncludeArchivedUnavailable } from '../../DocFlow.utils.scope'
import { isDocFlowActiveWidgetMappping } from '../../DocFlow.utils.widgets'
import { DOC_FLOW_BUILDING_FIELD_NAME } from '../../DocFlowBuildingField'
import { DOC_FLOW_BUILDING_GROUP_FIELD_NAME } from '../../DocFlowBuildingGroupField'
import { DocFlowMappingProcessCell } from '../../DocFlowMapping'
import {
  getMappingTypeOptions,
  isDocFlowWidgetMappingTypeContractId,
  isDocFlowWidgetMappingTypeName,
} from '../../DocFlowWidgets'
import { useDocFlowContext } from '../useDocFlowContext'
import { useDocFlowMappingContext } from '../useDocFlowMappingContext'
import { DocFlowMappingWidgetWithSelectedAreaNotificationType } from '../useDocFlowMappingWidgetWithSelectedArea'

export function useDocFlowMappingWidgetMappingFields({
  widgetDisabled,
  widgetNotifications,
  onChangeAccuracy,
  onClearFieldType,
  onDebounceWidgetGetSelectedAreaInfo,
  onGetMappingSelectedAreaInfo,
  onPrependNotification,
  onRemoveAdditionalNotifications,
  onRemoveNotification,
}) {
  const { docFlow } = useDocFlowContext()

  const {
    activeWidget,
    handleInitSelectArea,
    handleToggleOpenSelectAreaInfo,
    handleToggleSelectArea,
    isChanged,
    isChanging,
    isInitSelectArea,
    processData,
    selectedBox,
    showSelectArea,
  } = useDocFlowMappingContext()

  const isMappingWidgetActive = isDocFlowActiveWidgetMappping(activeWidget)

  const { setFieldValue, values } = useFormikContext()
  const typeValue = values[DocFlowMappingProcessCell.MappingType]
  const buildingValue = values[DocFlowMappingProcessCell.MappingBuilding]?.value
  const buildingGroupValue =
    values[DocFlowMappingProcessCell.MappingBuildingGroup]?.value

  const options = getMappingTypeOptions()

  const isContractId = isDocFlowWidgetMappingTypeContractId(typeValue?.value)

  const isName = isDocFlowWidgetMappingTypeName(typeValue?.value)

  const needToGetSelectAreaInfo = isChanged || isChanging

  function resetIncludeArchivedWithoutScope(building, buildingGroup) {
    if (
      isDocFlowMappingIncludeArchivedUnavailable({
        building,
        buildingGroup,
        docFlow,
      })
    ) {
      setFieldValue(DocFlowMappingProcessCell.MappingIncludeArchived, false)
    }
  }

  function handleGetMappingSelectedAreaInfo(updatedValues) {
    if (needToGetSelectAreaInfo) {
      onGetMappingSelectedAreaInfo({
        ...values,
        ...updatedValues,
      })
    }
  }

  function handleChangeType(value) {
    onRemoveAdditionalNotifications()

    if (value.value) {
      showSelectArea()

      if (!selectedBox) {
        handleToggleSelectArea(handleInitSelectArea, EMPTY_LIST)
      }

      handleGetMappingSelectedAreaInfo({
        [DocFlowMappingProcessCell.MappingType]: value,
      })
    }
  }

  function handelClearType() {
    onClearFieldType()

    if (isContractId || isName) {
      setFieldValue(DOC_FLOW_BUILDING_FIELD_NAME, FORMIK_EMPTY_VALUE)
    }
  }

  function handleChangeBuilding(value) {
    onRemoveAdditionalNotifications()
    handleToggleOpenSelectAreaInfo(false)
    resetIncludeArchivedWithoutScope(value?.value, buildingGroupValue)

    handleGetMappingSelectedAreaInfo({
      [DocFlowMappingProcessCell.MappingBuilding]: value?.value || null,
    })
  }

  function handleChangeBuildingGroup(value) {
    onRemoveAdditionalNotifications()
    handleToggleOpenSelectAreaInfo(false)
    resetIncludeArchivedWithoutScope(buildingValue, value?.value)

    handleGetMappingSelectedAreaInfo({
      [DocFlowMappingProcessCell.MappingBuildingGroup]: value?.value || null,
    })
  }

  function handleChangeIncludeArchived(value) {
    onRemoveAdditionalNotifications()
    handleToggleOpenSelectAreaInfo(false)

    handleGetMappingSelectedAreaInfo({
      [DocFlowMappingProcessCell.MappingIncludeArchived]: value,
    })
  }

  function handleChangeAccuracy(updatedValues) {
    if (needToGetSelectAreaInfo) {
      onChangeAccuracy({ ...values, ...updatedValues })
    }
  }

  useEffect(() => {
    if (
      isMappingWidgetActive &&
      !selectedBox &&
      processData?.[DocFlowMappingProcessCell.MappingArea]?.length === 0
    ) {
      onPrependNotification({
        ...widgetNotifications?.[
          DocFlowMappingWidgetWithSelectedAreaNotificationType.Phase
        ],
        type: NOTIFICATION_TYPE.info,
      })
    }
  }, [isMappingWidgetActive, processData, selectedBox, widgetNotifications])

  useEffect(() => {
    if (isMappingWidgetActive) {
      onRemoveAdditionalNotifications()

      if (isChanged) {
        onDebounceWidgetGetSelectedAreaInfo(values)

        onRemoveNotification(
          widgetNotifications?.[
            DocFlowMappingWidgetWithSelectedAreaNotificationType.NotChanged
          ].id
        )
      }
    }
  }, [isChanged, onRemoveAdditionalNotifications])

  useEffect(() => {
    if (
      processData?.[DocFlowMappingProcessCell.MappingArea].length > 0 &&
      isMappingWidgetActive &&
      selectedBox &&
      isInitSelectArea &&
      !isChanging &&
      isChanged === null &&
      !widgetDisabled
    ) {
      onGetMappingSelectedAreaInfo(values)
    }
  }, [
    isChanged,
    isChanging,
    isInitSelectArea,
    isMappingWidgetActive,
    processData,
    selectedBox,
    widgetDisabled,
  ])

  const buildingFieldApiParams = buildingGroupValue
    ? { [DOC_FLOW_BUILDING_GROUP_FIELD_NAME]: buildingGroupValue }
    : null

  const buildingGroupFieldApiParams = buildingValue
    ? { [DOC_FLOW_BUILDING_FIELD_NAME]: buildingValue }
    : null

  const includeArchivedDisabled = isDocFlowMappingIncludeArchivedUnavailable({
    building: buildingValue,
    buildingGroup: buildingGroupValue,
    docFlow,
  })

  return {
    buildingFieldApiParams,
    buildingGroupFieldApiParams,
    handelClearType,
    handleChangeAccuracy,
    handleChangeBuilding,
    handleChangeBuildingGroup,
    handleChangeIncludeArchived,
    handleChangeType,
    includeArchivedDisabled,
    isContractId,
    isName,
    options,
    typeValue,
  }
}
